import { FC, useState, MouseEventHandler, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dispatch } from "redux";
import { Button, Box, CSSObject, FormControl } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRangePicker, DateRange } from "@mui/x-date-pickers-pro";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import OccupantSelector, {
  Occupant,
} from "./OccupantSelector/OccupantSelector";
import { Today } from "@mui/icons-material";
import { DateTime } from "luxon";
import { saveSearch } from "../store/searchReducer";
import { formatDateForAPI } from "../utils/dateHelpers";

import "./Header/Header.scss";
import GooglePlaceAutoComplete from "./GooglePlaceAutoComplete";

import { parseISO } from "date-fns";

interface FilterBarProps {
  sx?: CSSObject;
  zoomed?: boolean;
  home?: boolean;
  city?: {
    city: { description: string };
    checkIn: string;
    checkOut: string;
    occupants: {
      adults: number;
      children: number;
      childrenAge: number[];
      dogs: number;
    };
    lat: number;
    lng: number;
  } | string;
  showText?: boolean;
  mapOnPage?: boolean;
  showButton?: boolean;
  setNumberOfPets?: (pets: number) => void;
}

function nextWeekday() {
  const today = DateTime.fromISO(new Date().toISOString());
  console.log(today);
  return today.plus({ days: (7 - today.weekday + 6) % 7 });
}

const LargeFilterBar: FC<FilterBarProps> = ({
  showText = false,
  city = "",
  mapOnPage = false,
  showButton = false,
}) => {
  const [isTextField, setIsTextField] = useState(false);
  const search: any = useSelector(
    (state: any) => state.searchReducer.search
  );
  
  // Handle both string and object formats for city prop
  const cityData = typeof city === 'object' && city !== null ? city : null;
  
  
  const [selectedCity, setSelectedCity] = useState(
    cityData 
      ? {
          city: cityData.city,
          lat: cityData.lat,
          lng: cityData.lng,
        }
      : typeof city === 'string' && city
      ? city
      : search.city
      ? {
          city: search.city,
          lat: search.lat,
          lng: search.lng,
        }
      : null
  );

  const [formError, setFormError] = useState({});
  const [open, setOpen] = useState(false);
  const [isAccept, setIsAccept] = useState(false);

const checkInMinimum =
  search?.checkIn && DateTime.fromISO(search.checkIn) < DateTime.now().startOf('day');

const [checkDate, setCheckDate] = useState<DateRange<Date | null>>([
  // Check-in date logic
  search.checkIn && !checkInMinimum
    ? parseISO(search.checkIn)
    : nextWeekday().toJSDate(),
  
  // Check-out date logic
  search.checkOut && search.checkIn && !checkInMinimum
    ? parseISO(search.checkOut)
    : search.checkIn && !checkInMinimum
    ? null   
    : nextWeekday().plus({ days: 1 }).toJSDate(),
]);

  // Extract dogs parameter from cityData (already passed from ListingPageNew)
  const dogsFromUrl = cityData?.occupants?.dogs;
  
  const [occupants, setOccupants] = useState(() => {
    // Priority: 1. cityData.occupants (contains URL dogs param), 2. Default
    if (cityData?.occupants) {
      return cityData.occupants;
    } else {
      return { 
        adults: 2, 
        children: 0, 
        dogs: 1  // Default fallback
      };
    }
  });

  const navigate = useNavigate();

  const dispatch: Dispatch<any> = useDispatch();

  const onOccupantChange = (value: Occupant) => {
    setOccupants(value);
  };

  const handleDateRangeClose = () => {
    setIsAccept(false);
    if (!isTextField) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) setIsTextField(false);
  }, [open]);

    // Log the dogs parameter if passed from ListingPageNew
  useEffect(() => {
    if (cityData?.occupants) {
      console.log('[LargeFilterBar] Pets info changed:');
      console.log('Dogs:', cityData.occupants.dogs);
      console.log('Full occupants:', cityData.occupants);
      console.log('Full city data:', cityData);
    }
    if (occupants) {
      console.log('[LargeFilterBar] Current occupants state:', occupants);
      console.log('[LargeFilterBar] Current dogs count:', occupants.dogs);
    }
  }, [cityData, occupants]);

  const handleFilterOutClick: MouseEventHandler<Element> = () => {
    // TagManager.dataLayer({ dataLayer: { event: "clicked_search" } });

    if (
      occupants.adults !== 0 &&
      selectedCity &&
      checkDate[0] &&
      checkDate[1]
    ) {
      const checkIn = formatDateForAPI(new Date(checkDate[0]));
      const checkOut = formatDateForAPI(new Date(checkDate[1]));
      setFormError("");
      
      // Handle both string and object types for selectedCity
      const cityInfo = typeof selectedCity === 'string' 
        ? { description: selectedCity }
        : selectedCity.city;
      const lat = typeof selectedCity === 'string' ? null : selectedCity.lat;
      const lng = typeof selectedCity === 'string' ? null : selectedCity.lng;
      
      dispatch(
        saveSearch({
          city: cityInfo,
          checkIn,
          checkOut,
          occupants,
          lat,
          lng,
        })
      );

      const urlParams = new URLSearchParams({
        city: cityInfo?.description || cityInfo,
        checkIn,
        checkOut,
        adults: occupants.adults.toString(),
        children: occupants.children.toString(),
        dogs: occupants.dogs.toString(),
        ...(lat && { lat: lat.toString() }),
        ...(lng && { lng: lng.toString() }),
      });
      
      // Add childrenAge parameter if there are children
      if (occupants.children > 0 && 'childrenAge' in occupants && occupants.childrenAge && occupants.childrenAge.length > 0) {
        urlParams.set('childrenAge', occupants.childrenAge.join(','));
      }
      
      const string = urlParams.toString();

      navigate(`/listings?${string}`);
    } else {
      alert("error");
      if (!selectedCity) {
        setFormError("Location required");
      }
      if (!checkDate[0]) {
        setFormError("Check-in date required");
      }
      if (
        checkDate[0] &&
        new Date(checkDate[0]) < new Date(new Date().setHours(0, 0, 0, 0))
      ) {
        setFormError("Check-in date must be today at the earliest");
      }
      if (!checkDate[1]) {
        setFormError("Check-out date required");
      }
      if (
        checkDate[1] &&
        checkDate[0] &&
        new Date(checkDate[1]) <= new Date(checkDate[0])
      ) {
        setFormError("Check-out date must be after check-in date");
      }
      if (occupants.adults === 0) {
        setFormError("Search must include at least 1 adult guest");
      }
    }
  };

  return (
    <Box
      sx={{
        mx: "auto",
        mt: "0em",
        zIndex: "20",
        maxWidth: "100%",
        width: "fit-content",
      }}
    >
      {showText && (
        <Box>
          <Box
            component="h1"
            sx={{ ml: "0.1em", mb: "0.5em" }}
            className="filter-bar-wrapper-title"
          >
            <b>Pet-friendly travel starts here</b>
          </Box>
          <Box
            component="h2"
            sx={{ ml: "0.3em" }}
            className="filter-bar-wrapper-desc"
          >
            Book pet friendly hotels, vacation rentals, and resorts at the lowest rates
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: "1.5em",
          border: "1px gray solid",
          boxSizing: "border-box",
          background: "#f5f3f3",
          zIndex: 11,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "6px",
        }}
        className="filter-bar-desktop"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "0.5rem",
            px: "0.75rem",
          }}
        >
          <Box
            sx={{
              background: "white",
              border: "1px solid #aaabab",
              borderRadius: "5px",
              maxWidth: {
                xs: 290, // default (extra-small)
                md: 290, // medium and up
              },
              minWidth: {
                xs: 200,
                md: 250,
              },
              width: "100%",
            }}
          >
            <FormControl fullWidth>
              <GooglePlaceAutoComplete
                width={"100%"}
                setSelectedCity={setSelectedCity}
                city={selectedCity}
                mapOnPage={mapOnPage}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              background: "white",
              maxWidth: {
                xs: 290, // default (extra-small)
                md: 290, // medium and up
              },
              minWidth: {
                xs: 200,
                md: 250,
              },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateRangePicker
                format="MMM dd"
                open={open}
                onAccept={() => setIsAccept(true)}
                onClose={handleDateRangeClose}
                onOpen={() => {
                  if (!isAccept) {
                    setOpen(true);
                  }
                }}
                calendars={2}
                // clearable={true}
                value={checkDate}
                minDate={new Date()}
                onChange={(newValue: DateRange<Date>) => {
                  setFormError("");
                  
                  if (newValue[0] && newValue[1]) {
                    const checkInDate = new Date(newValue[0]);
                    const checkOutDate = new Date(newValue[1]);
                    
                    // If same day is selected, set only check-in date and clear checkout
                    if (checkInDate.toDateString() === checkOutDate.toDateString()) {
                      setCheckDate([newValue[0], null]);
                      return;
                    }
                  }
                  
                  setCheckDate(newValue);
                }}
                slots={{
                  field: SingleInputDateRangeField,
                }}
                slotProps={{
                  textField: ({ position }) => {
                    const isStart = position == "start";

                    const startValue = checkDate[0]
                      ? DateTime.fromJSDate(new Date(checkDate[0])).toFormat(
                          "MMM dd"
                        )
                      : "Check-in date";
                    const endValue = checkDate[1]
                      ? DateTime.fromJSDate(new Date(checkDate[1])).toFormat(
                          "MMM dd"
                        )
                      : isStart
                      ? "Check-in date"
                      : "Check-out date";

                    return {
                      variant: "standard",
                      sx: {
                        // wrapper styles
                        display: "flex",
                        justifyContent: "flex-start",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "0.3rem",
                        height: "40px",
                        width: "100%",
                        // maxWidth: showText || showButton ? "290px" : "170px",
                        border: "1px solid #aaabab",
                        borderRadius: "5px",
                        cursor: "pointer",
                      },
                      InputProps: {
                        // internal input styles
                        value: `${startValue} - ${endValue}`,
                        startAdornment: (
                          <Box ml="0.75rem" mr="0.75rem" mt="0.4rem">
                            <Today fontSize="small" sx={{ color: "black" }} />
                          </Box>
                        ),
                        sx: {
                          color: "black",
                          fontFamily: "Lato",
                          textTransform: "none",
                          fontSize: "15px",
                          ["@media (max-width: 600px)"]: { fontSize: "1em" },
                          width: "100%",
                          border: "none",
                          cursor: "pointer",
                        },
                        disableUnderline: true,
                      },
                    };
                  },
                  actionBar: {
                    actions: ["clear"],
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box>
          <OccupantSelector
            value={occupants}
            onChange={onOccupantChange}
            variant="standard"
            size="small"
            fullWidth={false}
            sx={{
              maxWidth: showText || showButton ? "290px" : "auto",
              height: "40px",
              border: "1px solid #aaabab",
              borderRadius: "5px",
              background: "white",
              label: {
                fontFamily: "Lato",
                fontSize: "15px",
                color: "black",
              },
              input: {
                cursor: "pointer",
                fontFamily: "Lato",
                fontSize: "15px",
                color: "black",
                mt: "0.05rem",
              },
            }}
          />
        </Box>

        {showText ? (
          <Button
            onClick={handleFilterOutClick}
            variant="contained"
            size="medium"
            sx={{
              textTransform: "none",
              fontFamily: "Lato",
              m: "0.75em",
              p: "1rem",
              height: "41.5px",
            }}
            startIcon={<SearchIcon sx={{ height: "24px", fill: "white" }} />}
          >
            Search
          </Button>
        ) : (
          <Box
            onClick={handleFilterOutClick}
            sx={{
              cursor: "pointer",
              backgroundColor: "#03989E",
              borderRadius: "100%",
              p: "0.5rem",
              m: "0.5rem",
            }}
          >
            <SearchIcon
              sx={{ cursor: "pointer", height: "20px", fill: "white" }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { LargeFilterBar };
